import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {NON_EXECUTABLE_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import AnalysisGraphUtils from 'analysis-tool/flowchart/analysis-graph/AnalysisGraphUtils';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {readStepTypeInfosFromCache} from 'pipelines/hooks/useStepTypeInfosQuery';
import {CreateStepParams, PipelineEdgeUpsert} from 'pipelines/network/PipelineDAO';
import {PipelineEdge, PipelineStep, PipelineStepRawData, PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getExpectedStepDataKind, getInputStepDataShape} from 'pipelines/StepDataUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import {checkClipboardWriteAccess} from 'util/clipboard/ClipboardUtil';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {COPY_STEPS_CLIPBOARD_CONTENT_TYPE, CopyStepsClipboardContent} from './CopyPasteTypes';

/**
 * Translate a step to copy to a `CreateStepParams` upsert.
 *
 * @param step - The pipeline step to copy.
 * @param stepIndexByCopiedStepId - Index of copied steps' IDs to an index in the final array of steps.
 * @param columnMappingStepIndexByEdgeId - Index of incoming edges' IDs to the index of the corresponding
 *                                         APPLY_COLUMN_MAPPING step, in the final array of steps.
 * @returns A `CreateStepParams` step upsert.
 */
function translateStepToUpsert(
  step: PipelineStep,
  stepIndexByCopiedStepId: Record<string, number>,
  columnMappingStepIndexByEdgeId: Record<string, number>
): CreateStepParams {
  const incomingEdges = step.incomingEdges
    .map((edge): PipelineEdgeUpsert | null =>
      // The incoming edge points to another step being copied
      stepIndexByCopiedStepId[edge.fromStep.id] != null
        ? {
            fromStepIndex: stepIndexByCopiedStepId[edge.fromStep.id],
            fromStepDataName: edge.fromStepData.name,
            fromStepDataIndex: edge.fromStepDataIndex,
            toStepDataName: edge.toStepData.name,
            toStepDataIndex: edge.toStepDataIndex,
            condition: edge.condition,
          }
        : // The incoming edge should be remapped to a new APPLY_COLUMN_MAPPING step
          columnMappingStepIndexByEdgeId[AnalysisGraphUtils.getEdgeId(edge)] != null
          ? {
              fromStepIndex: columnMappingStepIndexByEdgeId[AnalysisGraphUtils.getEdgeId(edge)],
              fromStepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
              fromStepDataIndex: 0,
              toStepDataName: edge.toStepData.name,
              toStepDataIndex: edge.toStepDataIndex,
              condition: null,
            }
          : // The incoming edge maps to a step that is not being copied, and no APPLY_COLUMN_MAPPING step is
            // being added. This edge will be dropped.
            null
    )
    .filter((edge) => edge != null);

  // If an edge was dropped, clear out `toStepDataIndex`, to avoid conflicting indexes.
  if (incomingEdges.length < step.incomingEdges.length) {
    incomingEdges.forEach((edge) => {
      edge.toStepDataIndex = null;
    });
  }

  return {
    stepType: step.stepType,
    stepVersion: step.stepVersion,
    config: step.config ?? undefined,
    incomingEdges,
    // Try to run source steps immediately
    runAfterCreate: step.inputStepDatas.length === 0 && step.config != null,
  };
}

/**
 * Create an APPLY_COLUMN_MAPPING step upsert to replace an input data frame which comes from a step that
 * is not being copied.
 *
 * @param dataFrame - The data frame whose columns to map.
 * @returns A `CreateStepParams` step upsert.
 */
function createApplyColumnMappingUpsertForDataFrame(
  dataFrame: PipelineStepRawData<PipelineStepDataKind.DATASET>
): CreateStepParams {
  return {
    stepType: PipelineStepType.APPLY_COLUMN_MAPPING,
    stepVersion: 0,
    config: {
      outputColumns: dataFrame.columns.map((column) => ({
        matchBy: 'NAME',
        caseSensitive: false,
        displayName: column.displayName,
        systemName: column.systemName,
        createEmptyColumnIfNotFound: false,
      })),
      keepUnmappedColumns: false,
    },
    incomingEdges: [],
  };
}

interface ApplyColumnMappingUpsert {
  edge: PipelineEdge;
  stepUpsert: CreateStepParams;
  stepUpsertIndex: number;
}

/**
 * Create APPLY_COLUMN_MAPPING upserts to replace input data frames that come from a step that is not being
 * copied.
 *
 * Inserting an APPLY_COLUMN_MAPPING step is to make it easier to copy steps across pipelines, since different
 * pipelines are unlikely to have columns with exactly matching column system names.
 *
 * An APPLY_COLUMN_MAPPING upsert is added if (a) there is an edge from a not-copied step to a copied step,
 * (b) the `toStepData` is single-input, and (c) the `toStepData` has a data frame.
 *
 * An APPLY_COLUMN_MAPPING step is not added if the copied step is already an APPLY_COLUMN_MAPPING step.
 *
 * @param stepsToCopy - The steps being copied.
 * @param stepToCopyIds - IDs of the steps being copied.
 * @returns An `ApplyColumnMappingUpsert` for each cross-boundary single-input data frame edge.
 */
function createColumnMappingUpsertsForInputDataFrames(
  stepsToCopy: Array<PipelineStep>,
  stepToCopyIds: Set<string>
): Array<ApplyColumnMappingUpsert> {
  let stepUpsertIndex = 0;
  return stepsToCopy
    .filter((step) => step.stepType !== PipelineStepType.APPLY_COLUMN_MAPPING)
    .flatMap((step) =>
      step.incomingEdges
        .filter((edge) => !stepToCopyIds.has(edge.fromStep.id))
        .filter((edge) => getExpectedStepDataKind(step, edge.toStepData) === PipelineStepDataKind.DATASET)
        .filter(
          (edge) => !getInputStepDataShape(step.stepType, step.stepVersion, edge.toStepData.name).isMulti
        )
        .filter(
          (edge) => step.inputStepDatas.find(({id}) => id === edge.toStepData.id)?.rawDatas.length === 1
        )
        .map((edge) => ({
          edge,
          stepUpsert: createApplyColumnMappingUpsertForDataFrame(
            step.inputStepDatas.find(({id}) => id === edge.toStepData.id)!
              .rawDatas[0] as PipelineStepRawData<PipelineStepDataKind.DATASET>
          ),
          stepUpsertIndex: stepUpsertIndex++,
        }))
    );
}

/**
 * Copy steps to the user's clipboard, as pasteable step upserts.
 *
 * The step upserts include step types and step configs, but not step datas. Edges between copied steps are
 * maintained.
 *
 * If a single-input step data expects a data frame from a step that is not being copied, an
 * APPLY_COLUMN_MAPPING step will be added, to make it easier to paste the steps across pipelines.
 */
export async function copyStepsToClipboard(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  ...stepIds: Array<string>
): Promise<void> {
  // `checkClipboardWriteAccess` checks clipboard access, and shows a notification if access is denied.
  const hasClipboardWriteAccess = await checkClipboardWriteAccess();
  if (!hasClipboardWriteAccess) {
    return;
  }

  // Filter to steps that are being copied
  const stepsToCopyIds = new Set(stepIds);
  const stepsToCopy = pipeline.steps.filter((step) => stepsToCopyIds.has(step.id));
  const stepTypeInfos = readStepTypeInfosFromCache();

  // Block and inform the user if they attempt to copy a step type that cannot be copied.
  for (const step of stepsToCopy) {
    if ((NON_EXECUTABLE_STEP_TYPES as ReadonlyArray<PipelineStepType>).includes(step.stepType)) {
      notifications.error(
        `${getUserFacingStepTypeName(step.stepType, stepTypeInfos)} steps cannot be copied.`
      );
      return;
    }
  }

  // Create APPLY_COLUMN_MAPPING upserts to replace input data frames. This is to make it easier to copy
  // steps across pipelines, which are unlikely to have columns with exactly matching column system names.
  const columnMappingUpserts = createColumnMappingUpsertsForInputDataFrames(stepsToCopy, stepsToCopyIds);

  // Create mappings of step upserts' indexes, so that step upserts can reference each other by index.
  const columnMappingStepIndexByEdgeId = Object.fromEntries(
    columnMappingUpserts.map(({edge, stepUpsertIndex}) => [
      AnalysisGraphUtils.getEdgeId(edge),
      stepUpsertIndex,
    ])
  );
  const stepIndexByCopiedStepId = Object.fromEntries(
    stepsToCopy.map((step, index) => [step.id, columnMappingUpserts.length + index])
  );

  // Create an upsert for each step to copy
  const stepsToCopyStepUpserts = stepsToCopy.map((step) =>
    translateStepToUpsert(step, stepIndexByCopiedStepId, columnMappingStepIndexByEdgeId)
  );

  // Copy the step upserts to the user's clipboard
  const content: CopyStepsClipboardContent = {
    type: COPY_STEPS_CLIPBOARD_CONTENT_TYPE,
    steps: [...columnMappingUpserts.map(({stepUpsert}) => stepUpsert), ...stepsToCopyStepUpserts],
  };
  await copyToClipboard(JSON.stringify(content), {
    showToastOnSuccess: true,
    toastText:
      stepsToCopyStepUpserts.length === 1
        ? 'Copied 1 step to clipboard'
        : `Copied ${stepsToCopyStepUpserts.length} steps to clipboard`,
  });
}
